import {DestroyRef, Injectable, effect, inject, signal, untracked} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {ContextService} from '../context/context-signal.service';
import {AppEventsService} from '../app-events/app-events.service';
import {ViewContextService} from '../view-context/view-context.service';
import {ActionExecutor} from './action-executor.service';
import {DIALOG_API, DialogSessionPeek} from './dialog-api';
import {DialogDebugService} from './debug-mode.service';
import {RecipeAuthorJobResponse, RecipeAuthorJobService} from './recipe-author-job.service';
import {
  ChatMessage,
  ClarifyOption,
  DIALOG_CAPABILITIES,
  DialogToolProperties,
  DialogTurnRequest,
  DialogTurnResponse,
  RecipeSuggestionItem,
} from './dialog.models';

/** How often a `recipe_authoring` job is polled — the "recipe" role model can take up
 *  to ~40s, so this is deliberately loose, not latency-sensitive like the shopping-list
 *  sync poll. */
const RECIPE_JOB_POLL_INTERVAL_MS = 1500;

export type DialogStatus = 'idle' | 'sending' | 'awaiting_input' | 'error';

const DIALOG_LOCALE = 'de-CH';
const STORAGE_KEY = 'pantry.dialog.conversation';

interface StoredConversation {
  sessionId: string;
  context: string | null;
  messages: ChatMessage[];
}

function generateId(): string {
  return crypto.randomUUID();
}

@Injectable({providedIn: 'root'})
export class DialogService {
  private readonly dialogApi = inject(DIALOG_API);
  private readonly contextService = inject(ContextService);
  private readonly viewContextService = inject(ViewContextService);
  private readonly actionExecutor = inject(ActionExecutor);
  private readonly appEvents = inject(AppEventsService);
  private readonly dialogDebug = inject(DialogDebugService);
  private readonly recipeAuthorJobService = inject(RecipeAuthorJobService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(DialogService);

  /** One poll timer per in-flight recipe-authoring job, keyed by the chat message id
   *  that carries it — a household practically never has more than one open at once,
   *  but keying by message rather than a single shared timer costs nothing and avoids
   *  one job's poll silently clobbering another's. */
  private readonly recipeJobPollTimers = new Map<string, ReturnType<typeof setInterval>>();

  private readonly _messages = signal<ChatMessage[]>([]);
  readonly messages = this._messages.asReadonly();

  private readonly _status = signal<DialogStatus>('idle');
  readonly status = this._status.asReadonly();

  private readonly _isOpen = signal<boolean>(false);
  readonly isOpen = this._isOpen.asReadonly();

  /** Opaque, backend-owned. Never inspected or mutated, only round-tripped.
   *  Exposed read-only so the debug panel can display it live. */
  private readonly _context = signal<string | null>(null);
  readonly context = this._context.asReadonly();

  private readonly _sessionId = signal<string>(generateId());
  readonly sessionId = this._sessionId.asReadonly();

  /** Set when a peek discovers a live session on another `session_id` while this
   *  device already has a conversation of its own — the rejoin banner reads this to
   *  ask before silently jumping into it. `null` otherwise. */
  private readonly _rejoinPrompt = signal<DialogSessionPeek | null>(null);
  readonly rejoinPrompt = this._rejoinPrompt.asReadonly();

  constructor() {
    this.hydrate();

    this.appEvents.householdChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reset());

    // Mirrors the `loadShoppingLinks` effect in `AppComponent`: re-runs once household
    // context becomes available, and again on every later switch. Deliberately kept
    // separate from `reset()` above — one clears a session, this discovers one, and
    // they only happen to fire around the same household-switch moment.
    //
    // `peekSession` is called inside `untracked()` — without it, every signal it reads
    // (session id, messages) becomes a dependency of *this* effect too, since Angular
    // tracks all signal reads within an effect's call stack, not just the ones written
    // directly in its body. That turns an ordinary message send (which mutates
    // `_messages`) into a trigger for another peek — whose response can itself write
    // `_messages`/`_sessionId`, retriggering the effect again: a request storm.
    effect(() => {
      const householdId = this.contextService.context()?.household?.id;
      if (!householdId) return;
      untracked(() => this.peekSession(householdId));
    });

    this.destroyRef.onDestroy(() => this.stopAllRecipeJobPolling());
  }

  /** One-shot text to drop into the composer the next time the panel opens. Lets a
   *  caller open the assistant "primed" for a task (e.g. authoring a recipe) so the
   *  model gets a clear instruction instead of a blank chat. Consumed once, then cleared. */
  private readonly _prefill = signal<string | null>(null);
  readonly prefill = this._prefill.asReadonly();

  /** Per-surface tool configuration for the current authoring flow, set by an
   *  intent-specific `openWith(...)` entry point (e.g. the meal planner opts
   *  `propose_recipe` into `pantry: 'prefer'`). Attached to every turn while set;
   *  cleared whenever the panel is opened plainly or the session resets, so a
   *  surface's config never leaks into an unrelated conversation. */
  private readonly _toolProperties = signal<DialogToolProperties | null>(null);

  open(): void {
    this._toolProperties.set(null);
    this._isOpen.set(true);
  }

  /**
   * Open the panel with the composer pre-filled (editable, not auto-sent). Use for
   * intent-specific entry points — e.g. "create a recipe" buttons — so the first
   * utterance clearly asks for that, which is what makes the assistant call the right
   * tool (`propose_recipe`) and render its structured preview instead of chatting prose.
   *
   * `toolProperties` lets the entry point tell a tool how to behave on this surface
   * (see DialogToolProperties) — e.g. the meal planner authoring "with what I have"
   * passes `{propose_recipe: {pantry: 'prefer'}}`.
   */
  openWith(prefill: string, toolProperties?: DialogToolProperties): void {
    this._prefill.set(prefill);
    this._toolProperties.set(toolProperties ?? null);
    this._isOpen.set(true);
  }

  /** Read and clear the pending prefill. The panel calls this once it has applied it. */
  consumePrefill(): string | null {
    const value = this._prefill();
    if (value !== null) this._prefill.set(null);
    return value;
  }

  close(): void {
    this._isOpen.set(false);
  }

  toggle(): void {
    this._isOpen.update(v => !v);
  }

  reset(): void {
    const householdId = this.contextService.context()?.household?.id;
    const sessionId = this._sessionId();

    this.clearLocalSession();

    if (householdId) {
      this.dialogApi.invalidateSession(householdId, sessionId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: err => this.log.warn('Failed to invalidate dialog session (non-fatal)', err),
        });
    }
  }

  /** Clears local state only — no server call. Used both by `reset()` (which pairs it
   *  with an explicit `invalidateSession`) and by a `found: false` peek discovering
   *  this device's own conversation was invalidated/expired elsewhere, where there's
   *  nothing left server-side to invalidate. */
  private clearLocalSession(): void {
    this.stopAllRecipeJobPolling();
    this._context.set(null);
    this._sessionId.set(generateId());
    this._messages.set([]);
    this._status.set('idle');
    this._rejoinPrompt.set(null);
    this._toolProperties.set(null);
    this.persist();
  }

  async send(utterance: string): Promise<void> {
    const text = utterance.trim();
    if (!text || this._status() === 'sending') return;

    this.appendMessage({id: generateId(), role: 'user', text, createdAt: Date.now()});
    await this.performTurn(text);
  }

  /**
   * The brainstorm→commit handoff: the user tapped a recipe idea card (from a
   * `recipe_suggestions` artifact) to author it "for real". We hand the whole idea
   * to `propose_recipe` via `selected_idea` so the strong model writes *that* dish
   * rather than re-interpreting a bare title, and prefer the pantry since the ideas
   * were built from what's in stock. The one-shot tool properties ride the next turn
   * (cleared on the following plain open/reset like any other openWith config).
   *
   * Returns the id of the assistant message carrying the resulting `recipeJob`
   * (`''` if the turn couldn't even start, e.g. no household) — a caller that
   * needs to know when authoring finishes (e.g. `MealPlanStore` turning a pool
   * idea into a saved recipe) watches `messages()` for that id's `recipeJob`
   * to reach a terminal status.
   */
  async authorSuggestion(idea: RecipeSuggestionItem): Promise<string> {
    if (this._status() === 'sending') return '';

    this._toolProperties.set({
      propose_recipe: {
        pantry: 'prefer',
        selected_idea: {
          title: idea.title,
          description: idea.description,
          uses_from_stock: idea.uses_from_stock,
          missing_ingredients: idea.missing_ingredients,
        },
      },
    });

    const text = `Erstelle das Rezept „${idea.title}“`;
    this.appendMessage({id: generateId(), role: 'user', text, createdAt: Date.now()});
    return this.performTurn(text);
  }

  async selectOption(option: ClarifyOption): Promise<void> {
    if (this._status() === 'sending') return;

    this.markLastClarifySelected(option.index);
    this.appendMessage({id: generateId(), role: 'user', text: option.label, createdAt: Date.now()});
    await this.performTurn(String(option.index));
  }

  async retryLast(): Promise<void> {
    if (this._status() === 'sending') return;
    const lastUser = [...this._messages()].reverse().find(m => m.role === 'user' && m.retryable);
    if (!lastUser) return;

    this.patchMessage(lastUser.id, {retryable: false});
    await this.performTurn(lastUser.text);
  }

  /** Answers an open `confirm` question. The answer is sent as an ordinary utterance —
   *  the client does not classify what "ja"/"nein" means, the backend does. */
  async confirmAction(answer: 'yes' | 'no'): Promise<void> {
    if (this._status() === 'sending') return;

    const text = answer === 'yes' ? 'Ja' : 'Nein';
    this.markLastConfirmAnswered(answer);
    this.appendMessage({id: generateId(), role: 'user', text, createdAt: Date.now()});
    await this.performTurn(text);
  }

  /** Sends a cancel word as an ordinary utterance. The backend short-circuits on it
   *  before any model call, so this is guaranteed fast and costs no turn budget. */
  async cancel(): Promise<void> {
    if (this._status() === 'sending') return;

    const text = 'Abbrechen';
    this.appendMessage({id: generateId(), role: 'user', text, createdAt: Date.now()});
    await this.performTurn(text);
  }

  /** Adopts the pending cross-device session offered by `rejoinPrompt()`. */
  acceptRejoin(): void {
    const peek = this._rejoinPrompt();
    if (peek) this.adoptSession(peek);
  }

  /** Declines the pending cross-device session — local state is left untouched, the
   *  discovered session is left alone and simply expires on its own idle TTL. */
  dismissRejoin(): void {
    this._rejoinPrompt.set(null);
  }

  private peekSession(householdId: number): void {
    // Snapshot local state as of *now* — the moment the peek round-trip starts, not
    // whenever its (possibly slow) response happens to land. Reading `_messages`/
    // `_sessionId` live in the response handler would race a user who starts typing
    // (or a first, unrelated send()) while the peek is still in flight: `_messages`
    // would go non-empty for a reason that has nothing to do with a stale/discovered
    // session, and reconciling against that live value would wrongly clobber it.
    const local = {sessionId: this._sessionId(), hadMessages: this._messages().length > 0};

    this.dialogApi.peekActiveSession(householdId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: peek => this.handlePeekResult(peek, local),
        error: err => this.log.warn('Failed to check for an active dialog session (non-fatal)', err),
      });
  }

  private handlePeekResult(peek: DialogSessionPeek, local: {sessionId: string; hadMessages: boolean}): void {
    // Never reconcile over an actively in-flight turn — whatever the peek found, it
    // was already stale the moment the user started a new one.
    if (this._status() === 'sending') return;

    if (!peek.found) {
      // Nothing persists past invalidate/expiry/sweep server-side. If this device
      // already had a conversation *before* this peek was sent, that session is gone
      // — invalidated by another device (see `reset()`) or expired on its own. A
      // device that simply hadn't talked to the backend yet has nothing stale to
      // clear, so it's left untouched.
      if (local.hadMessages) {
        this.clearLocalSession();
      }
      return;
    }
    if (!peek.session_id) return;

    if (peek.session_id === local.sessionId) {
      // Same device rediscovering its own still-active session (e.g. sessionStorage
      // was empty after a reload, but the backend row is still alive) — just restore
      // what was already this device's, no user-facing decision needed.
      this.adoptSession(peek);
      return;
    }

    // Cross-device: a live conversation exists under a different session_id. Auto-adopt
    // only if there was nothing local to lose *before this peek started*; otherwise ask
    // before silently discarding this device's own in-progress conversation.
    if (!local.hadMessages) {
      this.adoptSession(peek);
    } else {
      this._rejoinPrompt.set(peek);
    }
  }

  private adoptSession(peek: DialogSessionPeek): void {
    if (!peek.session_id) return;
    this._sessionId.set(peek.session_id);
    this._messages.set((peek.transcript ?? []).map(entry => ({
      id: generateId(),
      role: entry.role,
      text: entry.text,
      createdAt: new Date(entry.at).getTime(),
    })));
    this._status.set(peek.state === 'idle' || !peek.state ? 'idle' : 'awaiting_input');
    this._rejoinPrompt.set(null);
    this.persist();
  }

  /** Returns the assistant placeholder message's id, so a caller (e.g.
   *  `authorSuggestion`) can later find that message and read its `recipeJob`. */
  private async performTurn(utterance: string): Promise<string> {
    const householdId = this.contextService.context()?.household?.id;
    if (!householdId) {
      this.appendMessage({
        id: generateId(),
        role: 'error',
        text: 'Kein Haushalt ausgewählt.',
        createdAt: Date.now(),
      });
      this._status.set('error');
      return '';
    }

    // A message from an earlier, abandoned failure must not linger as "retryable" —
    // otherwise a later failure that isn't itself retryable (e.g. 401/403) could leave
    // `retryLast()` resending that stale message instead of doing nothing.
    this.clearRetryableFlags();

    const pendingId = generateId();
    this.appendMessage({id: pendingId, role: 'assistant', text: '', pending: true, createdAt: Date.now()});
    this._status.set('sending');
    this.persist();

    const viewContext = this.viewContextService.snapshot();
    const toolProperties = this._toolProperties();
    const request: DialogTurnRequest = {
      utterance,
      context: this._context(),
      session_id: this._sessionId(),
      channel: 'text',
      capabilities: DIALOG_CAPABILITIES,
      locale: DIALOG_LOCALE,
      ...(viewContext ? {view_context: viewContext} : {}),
      ...(toolProperties ? {tool_properties: toolProperties} : {}),
    };

    const startedAt = performance.now();
    try {
      const response = await new Promise<DialogTurnResponse>((resolve, reject) => {
        this.dialogApi.turn(householdId, request).subscribe({next: resolve, error: reject});
      });
      await this.handleResponse(response, request, pendingId, householdId, performance.now() - startedAt);
    } catch (err) {
      this.handleError(err, pendingId);
    }
    return pendingId;
  }

  private async handleResponse(
    response: DialogTurnResponse,
    request: DialogTurnRequest,
    pendingId: string,
    householdId: number,
    latencyMs: number,
  ): Promise<void> {
    this._context.set(response.context);
    const isConfirm = response.state === 'confirm';

    this.patchMessage(pendingId, {
      text: response.message,
      pending: false,
      clarify: response.clarify,
      confirm: isConfirm || undefined,
      action: response.action,
      affected: response.affected,
      artifacts: response.artifacts,
      debug: this.dialogDebug.active()
        ? {
            state: response.state,
            expectsInput: response.expects_input,
            context: response.context,
            action: response.action,
            failure: response.failure,
            affected: response.affected,
            latencyMs: Math.round(latencyMs),
            request,
            response,
          }
        : undefined,
    });

    // `expects_input` is the authoritative terminal discriminator — not `state`. This
    // way an unknown future state still degrades correctly (renders, ends cleanly)
    // instead of wedging the session, and `state` is read only for rendering from here on.
    const isTerminal = !response.expects_input;
    if (isTerminal) {
      this._context.set(null);
      this._status.set('idle');
    } else {
      this._status.set('awaiting_input');
    }

    this.persist();

    // propose_recipe registered a background authoring job instead of a draft —
    // nothing to dispatch, just start polling so this message updates itself once the
    // draft (or a failure) is ready. See RecipeAuthorJobService/ChatMessageRecipeJob.
    if (response.state === 'recipe_authoring' && response.recipe_job) {
      this.patchMessage(pendingId, {
        recipeJob: {jobId: response.recipe_job.job_id, status: 'running'},
      });
      this.startPollingRecipeJob(pendingId, householdId, response.recipe_job.job_id);
    }

    // While `confirm` is open, `action` is display data for the question only — never
    // dispatched. Dispatch happens on the *next* turn, once the backend has decided.
    if (isConfirm || !response.action) {
      return;
    }

    if (!response.action.executed) {
      // Outside `confirm`, an unexecuted write action violates the backend invariant
      // that writes are always executed server-side. Log it; don't show the user
      // "not supported" — that message would simply be wrong.
      this.log.warn('Dialog action not executed outside confirm state', {
        actionType: response.action.type,
        state: response.state,
      });
      return;
    }

    const outcome = await this.actionExecutor.handle(response.action, {householdId}, response.affected);
    if (outcome.kind === 'error') {
      this.appendMessage({id: generateId(), role: 'error', text: outcome.message, createdAt: Date.now()});
    } else if (outcome.kind === 'unsupported') {
      this.appendMessage({id: generateId(), role: 'system', text: outcome.message, createdAt: Date.now()});
    }
    this.persist();
  }

  private handleError(err: unknown, pendingId: string): void {
    this._status.set('error');

    const status = err instanceof HttpErrorResponse ? err.status : 0;
    let text: string;
    let retryable = false;

    if (status === 401 || status === 403) {
      text = 'Bitte melde dich erneut an, um den Assistenten zu nutzen.';
    } else if (status === 503 || status === 0) {
      text = 'Der Assistent ist gerade nicht erreichbar.';
      retryable = true;
    } else if (status === 400) {
      // A 400 here means a malformed request or a tampered/expired/foreign-household
      // `context`. Per the backend contract, the safe recovery is to retry the same
      // utterance with `context: null` — clear it now so the retry starts fresh
      // instead of looping on the same stale context.
      text = 'Etwas ist schiefgelaufen. Bitte versuche es erneut.';
      retryable = true;
      this._context.set(null);
    } else {
      text = 'Etwas ist schiefgelaufen. Bitte versuche es erneut.';
      retryable = true;
    }

    this.log.error('Dialog turn failed', err);
    this.removeMessage(pendingId);
    this.appendMessage({id: generateId(), role: 'error', text, createdAt: Date.now()});

    if (retryable) {
      const lastUser = [...this._messages()].reverse().find(m => m.role === 'user');
      if (lastUser) this.patchMessage(lastUser.id, {retryable: true});
    }

    this.persist();
  }

  /**
   * Persists a `draft_ready` recipe-authoring job's draft as a real recipe — the
   * chat-side "Rezept speichern" button, mirroring RecipeImportService's own save
   * button. This is a direct, one-shot call: unlike every other write, there is no
   * `PendingAction`/confirm-turn behind it (see recipeauthor.Service.SaveDraft's
   * backend doc comment for why), so a failure here is surfaced as an inline error
   * message rather than a dialog turn error.
   */
  async saveRecipeJobDraft(messageId: string, jobId: string): Promise<void> {
    const householdId = this.contextService.context()?.household?.id;
    if (!householdId) return;

    try {
      const res = await new Promise<RecipeAuthorJobResponse>((resolve, reject) => {
        this.recipeAuthorJobService.saveJob(householdId, jobId).subscribe({next: resolve, error: reject});
      });
      const job = res.job;
      this.patchMessage(messageId, {
        recipeJob: {
          jobId,
          status: 'succeeded',
          draft: job.draft ?? undefined,
          createdRecipeId: job.created_recipe_id,
        },
      });
      this.persist();
      // This save bypasses the dialog Affected mechanism entirely (no turn involved),
      // so the recipes page needs this explicit nudge to refresh — same reasoning as
      // recipes.component.ts calling loadRecipes() right after saveImportedDraft().
      this.appEvents.notifyRecipeChanged();
    } catch (err) {
      this.log.error('Failed to save recipe author job draft', err, {jobId});
      this.appendMessage({
        id: generateId(),
        role: 'error',
        text: 'Das Rezept konnte nicht gespeichert werden. Bitte versuche es erneut.',
        createdAt: Date.now(),
      });
      this.persist();
    }
  }

  private startPollingRecipeJob(messageId: string, householdId: number, jobId: string): void {
    this.stopPollingRecipeJob(messageId);

    const pollOnce = () => {
      this.recipeAuthorJobService.getJob(householdId, jobId).subscribe({
        next: res => {
          const job = res.job;
          if (job.status === 'draft_ready') {
            this.stopPollingRecipeJob(messageId);
            this.patchMessage(messageId, {
              recipeJob: {jobId, status: 'draft_ready', draft: job.draft ?? undefined},
            });
            this.persist();
          } else if (job.status === 'failed') {
            this.stopPollingRecipeJob(messageId);
            this.patchMessage(messageId, {
              recipeJob: {jobId, status: 'failed', errorMessage: job.state_message},
            });
            this.persist();
          }
          // 'running': keep polling. 'succeeded' only happens via saveRecipeJobDraft,
          // which already patches the message itself — polling never sees it land
          // there first since it stops as soon as 'draft_ready' is seen.
        },
        error: err => this.log.warn('Failed to poll recipe author job (will retry)', {err, jobId}),
      });
    };

    pollOnce();
    this.recipeJobPollTimers.set(messageId, setInterval(pollOnce, RECIPE_JOB_POLL_INTERVAL_MS));
  }

  private stopPollingRecipeJob(messageId: string): void {
    const timer = this.recipeJobPollTimers.get(messageId);
    if (timer != null) {
      clearInterval(timer);
      this.recipeJobPollTimers.delete(messageId);
    }
  }

  private stopAllRecipeJobPolling(): void {
    for (const timer of this.recipeJobPollTimers.values()) clearInterval(timer);
    this.recipeJobPollTimers.clear();
  }

  private markLastClarifySelected(index: number): void {
    const messages = this._messages();
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].clarify) {
        this.patchMessage(messages[i].id, {selectedOptionIndex: index});
        return;
      }
    }
  }

  private markLastConfirmAnswered(answer: 'yes' | 'no'): void {
    const messages = this._messages();
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].confirm) {
        this.patchMessage(messages[i].id, {confirmAnswer: answer});
        return;
      }
    }
  }

  private clearRetryableFlags(): void {
    this._messages.update(msgs => msgs.map(m => (m.retryable ? {...m, retryable: false} : m)));
  }

  private appendMessage(message: ChatMessage): void {
    this._messages.update(msgs => [...msgs, message]);
  }

  private patchMessage(id: string, patch: Partial<ChatMessage>): void {
    this._messages.update(msgs => msgs.map(m => (m.id === id ? {...m, ...patch} : m)));
  }

  private removeMessage(id: string): void {
    this._messages.update(msgs => msgs.filter(m => m.id !== id));
  }

  private persist(): void {
    if (typeof sessionStorage === 'undefined') return;
    const payload: StoredConversation = {
      sessionId: this._sessionId(),
      context: this._context(),
      messages: this._messages(),
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      this.log.warn('Failed to persist dialog conversation', {err});
    }
  }

  private hydrate(): void {
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    try {
      const stored = JSON.parse(raw) as StoredConversation;
      this._sessionId.set(stored.sessionId ?? generateId());
      this._context.set(stored.context ?? null);
      this._messages.set(stored.messages ?? []);
    } catch (err) {
      this.log.warn('Failed to hydrate dialog conversation', {err});
    }
  }
}
